import { parse } from 'zipson'
import { getEnumValues } from 'model'
import { GridId, GridStore } from './gridTypes'
import { GridActions, GridActionTypes } from './gridActions'
import { EntityType, EntityUpdateAction, ServerActionTypes } from 'api'

export const gridDefault = {
  default: {
    name: 'default',
    columnSettings: { viewId: 'default', columnState: {} }
  }
}

export const initialState: GridStore = getEnumValues(GridId).reduce(
  (result, gridId) => {
    result[gridId] = {
      ...gridDefault
    }
    return result
  },
  {
    global: {
      showOrgActivity: false,
      showMaturedCollaterals: false,
      hideExpiredRejectedInInitiated: false,
      showTerminatedRepos: false,
      showOnlyEnabledUsers: true
    }
  }
)

type GridReducerActions = GridActions | EntityUpdateAction

export function gridReducer(state: GridStore = initialState, action: GridReducerActions): GridStore {
  switch (action.type) {
    case GridActionTypes.GRID_INITIALIZE: {
      return {
        ...state,
        ...action.payload
      }
    }

    case GridActionTypes.GRID_UPDATE_SETTINGS: {
      return {
        ...state,
        [action.payload.gridId]: {
          ...state[action.payload.gridId],
          [action.payload.settingsId]: action.payload.settings
        }
      }
    }

    case GridActionTypes.GRID_DELETE_VIEW: {
      const { gridId, gridSettingsId } = action.payload
      const allGridSettings = state[gridId]
      delete allGridSettings[gridSettingsId]
      return {
        ...state,
        [action.payload.gridId]: {
          ...allGridSettings
        }
      }
    }

    case GridActionTypes.SAVE_COLUMN_SETTINGS: {
      const { gridId, gridSettingsId, columnSettings } = action.payload

      return {
        ...state,
        [gridId]: !state[gridId]
          ? { ...gridDefault }
          : {
              ...state[gridId],
              [gridSettingsId]: {
                ...state[gridId][gridSettingsId],
                columnSettings: {
                  ...state[gridId][gridSettingsId].columnSettings,
                  ...columnSettings
                }
              }
            }
      }
    }

    case GridActionTypes.SAVE_COLUMN_COPY_SETTINGS: {
      const { gridId, gridSettingsId, columnCopySelections } = action.payload

      return {
        ...state,
        [gridId]: !state[gridId]
          ? { ...gridDefault }
          : {
              ...state[gridId],
              [gridSettingsId]: {
                ...state[gridId][gridSettingsId],
                columnCopySelections
              }
            }
      }
    }

    case GridActionTypes.SAVE_BULK_DATE_UPDATE_FIELDS: {
      const { gridId, gridSettingsId, bulkDateUpdateFields } = action.payload

      return {
        ...state,
        [gridId]: !state[gridId]
          ? { ...gridDefault }
          : {
              ...state[gridId],
              [gridSettingsId]: {
                ...state[gridId][gridSettingsId],
                bulkDateUpdateFields
              }
            }
      }
    }

    case GridActionTypes.SAVE_COLLAPSED_ROW_IDS: {
      const { gridId, gridSettingsId, collapsedRowKeys } = action.payload

      return {
        ...state,
        [gridId]: !state[gridId]
          ? { ...gridDefault }
          : {
              ...state[gridId],
              [gridSettingsId]: {
                ...state[gridId][gridSettingsId],
                collapsedRowKeys
              }
            }
      }
    }

    case GridActionTypes.SAVE_SELECTED_ITEM_IDS: {
      const { gridId, gridSettingsId, selectedItemIds } = action.payload

      return {
        ...state,
        [gridId]: !state[gridId]
          ? { ...gridDefault }
          : {
              ...state[gridId],
              [gridSettingsId]: {
                ...state[gridId][gridSettingsId],
                selectedItemIds
              }
            }
      }
    }

    /**
     * This action is intercepted to populate the GridStore with the persisted settings from the server.
     * The data is decoded using the zipson (https://github.com/jgranstrom/zipson) library which compresses
     * JSON to a much terser format. This is required to stay within the message limit in the server
     */
    case ServerActionTypes.ENTITY_UPDATE: {
      if (action.payload.update.entityType === EntityType.USER_SETTINGS) {
        const {
          payload: {
            update: {
              entity: { payload }
            }
          }
        } = action
        return !payload ? state : (parse(payload) as GridStore)
      }

      return state
    }

    case GridActionTypes.SAVE_GLOBAL_GRID_SETTINGS: {
      const { globalGridSettings } = action.payload

      return {
        ...state,
        global: {
          ...state.global,
          ...globalGridSettings
        }
      }
    }

    default: {
      return state
    }
  }
}
